import { useMemo, useState } from 'react';
import type { AlignedHistory, HistoryRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Skeleton } from '#client/components/ui/Skeleton';
import { formatDate, dash } from '#client/lib/format';
import { routes } from '#client/lib/routes';
import { HistoryTable } from './HistoryTable';
import { includedVisitIds, toggleExcluded } from './report-history.logic';
import { useReportHistory } from './useReportHistory';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/**
 * The patient's earlier results for this test, beside the current ones.
 *
 * Its own card and its own fetch, between Results and Comments.
 *
 * **`useResultEntry` is deliberately untouched.** Which visits to print is a
 * *print* choice, and folding the selection into the entry form's state would
 * imply it gets saved with the report — when `draftPayload` and
 * `completePayload` are this app's statement of what a save means.
 * `ReportResultsTable` is untouched too: adding columns there would fight the
 * keyboard model, whose Enter/Arrow contract depends on row index matching
 * input index.
 */
export function ReportHistoryCard({
  reportId,
  patientId,
  onSelectionChange,
}: {
  reportId: number;
  patientId: number;
  /** The visits ticked for printing. Held by the print screen, not by the form. */
  onSelectionChange?: (reportIds: number[]) => void;
}) {
  const { data, loading, error } = useReportHistory(reportId, 6);

  const [excluded, setExcluded] = useState<Set<number>>(new Set());

  // Memoised so the fallback array is not a fresh identity on every render,
  // which would re-run the selection memo below for nothing.
  const visits = useMemo(() => data?.history.visits ?? [], [data]);
  const chosen = useMemo(() => includedVisitIds(visits, excluded), [visits, excluded]);

  function handleToggle(visitReportId: number, included: boolean) {
    const next = toggleExcluded(excluded, visitReportId, included);
    setExcluded(next);
    onSelectionChange?.(includedVisitIds(visits, next));
  }

  if (loading) {
    return (
      <Card title="Previous results">
        <Skeleton height="2rem" />
        <Skeleton height="6rem" />
      </Card>
    );
  }

  if (error || !data) return null;

  if (visits.length === 0) {
    return (
      <Card title="Previous results">
        <EmptyState
          title="No earlier visits for this test"
          body="Once this patient has had it before, the last results appear here for comparison."
        />
      </Card>
    );
  }

  const { rows, changed_ranges: changedRanges } = data.history;
  const live = rows.filter((row) => !row.retired);
  const retired = rows.filter((row) => row.retired);

  return (
    <Card
      title="Previous results"
      subtitle={`${visits.length} earlier ${visits.length === 1 ? 'visit' : 'visits'} of ${data.test_name}`}
      actions={
        <ButtonLink
          variant="secondary"
          size="sm"
          to={routes.patientTrends(patientId, data.test_id)}
        >
          Compare all visits
        </ButtonLink>
      }
      flush
    >
      <HistoryTable visits={visits} rows={live} />

      {retired.length > 0 && <RetiredRows rows={retired} />}

      {changedRanges.length > 0 && (
        <p className="c-field__help c-history__note">
          * The reference range has changed since an earlier visit for {changedRanges.join(', ')}.
          Each result is shown against the range that applied at its own visit.
        </p>
      )}

      {onSelectionChange && (
        <PrintPicker
          visits={visits}
          excluded={excluded}
          chosenCount={chosen.length}
          onToggle={handleToggle}
        />
      )}
    </Card>
  );
}

/** Which earlier visits go on the printed report. A print choice, never saved. */
function PrintPicker({
  visits,
  excluded,
  chosenCount,
  onToggle,
}: {
  visits: AlignedHistory['visits'];
  excluded: ReadonlySet<number>;
  chosenCount: number;
  onToggle: (reportId: number, included: boolean) => void;
}) {
  return (
    <div className="c-history__picker">
      <p className="c-detail-list__label">Include on the printed report</p>
      {visits.map((visit) => (
        <Checkbox
          key={visit.report_id}
          label={formatDate(visit.visited_at)}
          checked={!excluded.has(visit.report_id)}
          onChange={(event) => onToggle(visit.report_id, event.target.checked)}
        />
      ))}
      <p className="c-field__help">
        {chosenCount === 0
          ? 'No previous results will be printed.'
          : `${chosenCount} of ${visits.length} will be printed. This is a choice for this print, like the page size — it is not saved with the report.`}
      </p>
    </div>
  );
}

/** Parameters the test no longer has, shown compactly under the table. */
function RetiredRows({ rows }: { rows: readonly HistoryRow[] }) {
  return (
    <div className="c-history__retired">
      <p className="c-detail-list__label">No longer part of this test</p>
      <ul className="c-plain-list">
        {rows.map((row) => (
          <li key={row.parameter_id} className="c-plain-list__row">
            <span className="c-plain-list__name">{row.name}</span>
            <span className="c-plain-list__value">
              {row.cells.map((cell) => dash(cell?.result)).join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
